import type { KeyboardEventHandler } from 'react'
import { SearchIcon } from './Icons'
import { X } from 'lucide-react'

interface SearchInputProps {
  placeholder?: string
  className?: string
  inputClassName?: string
  variant?: 'default' | 'boxed' | 'header' | 'filled'
  value?: string
  onChange?: (value: string) => void
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  onFocus?: () => void
  onClear?: () => void
}

function cleanInputClassName(className: string): string {
  if (!className) return ''
  return className
    .split(' ')
    .filter((c) => {
      const name = c.trim()
      if (!name) return false
      // Remove height overrides to enforce consistency
      if (name.startsWith('h-') || name.startsWith('max-h-') || name.startsWith('min-h-')) return false
      // Remove padding left overrides to enforce icon clearance
      if (name.startsWith('pl-')) return false
      // Remove placeholder overrides to enforce design tokens
      if (name.startsWith('placeholder:')) return false
      // Remove text size overrides to enforce 14px text
      if (name === 'text-xs' || name === 'text-sm' || name === 'text-base' || name.startsWith('text-[')) return false
      return true
    })
    .join(' ')
}

export function SearchInput({
  placeholder = 'Search',
  className = '',
  inputClassName = '',
  variant = 'default',
  value,
  onChange,
  onKeyDown,
  onFocus,
  onClear,
}: SearchInputProps) {
  // Preserve responsiveness for header variant, enforce max-w-[301px] default only for standard inputs
  const labelClassName =
    variant === 'header'
      ? (className || 'w-full')
      : className
        ? (className.includes('w-') && !className.includes('max-w-') && !className.includes('w-[')
            ? `${className} max-w-[301px]`
            : className)
        : 'w-full max-w-[301px]'

  const cleanedInputClassName = cleanInputClassName(inputClassName)
  const placeholderClass = 'placeholder:text-slate-400'
  const iconColor = variant === 'header' ? 'text-[#111111]' : 'text-[#8894A2]'
  const iconPositionClass = variant === 'header' ? 'left-[16px] top-[12px]' : 'left-[18px] top-[12px]'

  const baseInputClassName =
    variant === 'filled'
      ? `h-[44px] w-full rounded-[10px] border border-transparent bg-[#f3f4f6] pl-[45px] pr-10 text-[14px] text-slate-700 outline-none transition ${placeholderClass} placeholder:font-normal placeholder:font-sans placeholder:leading-[20px] hover:bg-[#eef0f4] focus:bg-white focus:border-[#d1d5db] focus:ring-2 focus:ring-slate-200`
      : variant === 'header'
        ? `h-[44px] w-full rounded-[7px] border-0 bg-[#f3f0f0] pl-[44px] pr-10 text-[15px] text-[#111111] outline-none transition placeholder:text-[#111111] placeholder:font-normal placeholder:font-sans placeholder:leading-[20px] focus:bg-[#f3f0f0] focus:outline-none focus:ring-0`
        : variant === 'boxed'
          ? `h-[44px] w-full rounded-[8px] border border-[#dde6f1] bg-white pl-[45px] pr-10 text-[14px] text-slate-700 shadow-[0_4px_16px_rgba(15,23,42,0.04)] outline-none transition ${placeholderClass} placeholder:font-normal placeholder:font-sans placeholder:leading-[20px] hover:border-[#cfdae7] hover:bg-white focus:border-[#c7d4e4] focus:ring-4 focus:ring-[#dfe8f3]`
          : `h-[44px] w-full rounded-[8px] border border-[#d1d5db] bg-white pl-[45px] pr-3 text-[14px] text-slate-700 outline-none transition ${placeholderClass} placeholder:font-normal placeholder:font-sans placeholder:leading-[20px] hover:border-[#146971] hover:bg-[#f8fbff] focus:border-[#146971] focus:ring-4 focus:ring-[#146971]/10`

  return (
    <label className={`relative block ${labelClassName}`}>
      <SearchIcon className={`pointer-events-none absolute h-[20px] w-[20px] ${iconPositionClass} ${iconColor}`} />
      <input
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        className={`${baseInputClassName} ${cleanedInputClassName}`}
      />
      {value && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="absolute right-[14px] top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
          aria-label="Clear search input"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </label>
  )
}
